// @givmo/fundraisers-module — a game's Bonus pledges tab (FCM-WEB35; its mockups RULED as D-FCM-77, 2026-11-03,
// revision 7, decisions 1 to 8 as recommended). A bonus pledge (D-FCM-70, D-GFU-66) pays one amount only if
// something happens in the game: a moment its staff settle ("Soto gets the game-winning hit"), or the game's own
// score reaching a number ("the Herons score at least 5 runs"). The staff add one mid-game in one act (FCM-BE180:
// its fundraiser under the game, published, its offer and, for a moment, its yes/no), watch each one's state as
// fans see it, stop its pledging, and settle a moment. Every state and figure is the server's (the game page's
// read, and the game's overview for what each one's fundraiser was given); every refusal is the server's
// sentence with what to do next. The newest sits first, and an act changes a row's state where it stands.
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { charitiesGetCharitiesOptions } from '@givmo/api-client/query';
import {
  firstErrorCode,
  isGivmoError,
  type OrgFundraiserOut,
  type PledgeOfferCreate,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardHeader,
  Dialog,
  ErrorMessage,
  Field,
  Icon,
  RadioTile,
  RadioTileGroup,
  SearchInput,
  Select,
  cn,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { Check, Clock3, Plus } from 'lucide-react';
import { campaignOptions } from './campaign-queries';
import { formatFundraiserMoney } from './fundraisers-decode';
import { useFundraisersModule } from './fundraisers-context';
import { FundraisersLoading } from './fundraisers-state';
import { PHONE_HIT } from './lifecycle-ui';
import { publicCampaignOptions } from './layers-queries';
import { useRecipientNames } from './recipient-names';
import { extractCharityResults } from './recipients-decode';
import { askAgain, untilAnswered } from './season-setup-decode';
import { useSettled } from './season-setup-page';
import { useLivePage } from './use-live-page';
import { BONUS_COPY as B, BONUS_SERVER_WORDS } from './bonus-pledges-copy';
import {
  bonusRows,
  canSettle,
  gameCharityOf,
  gameCountOf,
  gameEnded,
  gameStillCounts,
  previewSentenceOf,
  unitsOf,
  wholeDollars,
  type BonusRow,
  type BonusStateKind,
  type GameCount,
} from './bonus-pledges-decode';
import {
  addBonusPledge,
  lockMomentResult,
  previewBonusSentence,
  stopBonusPledging,
  type BonusPledgeBody,
} from './bonus-pledges-transport';

/** The fans' card's berry (D-FCM-70's decision 10, ruled 2026-10-27 13:32), as the game page draws it
 *  (`apps/donate/app/components/game-page.tsx`, `BONUS_FILL` and `BONUS_BUTTON`). */
const BONUS_FILL = 'bg-[#BE1E9A] bg-[linear-gradient(90deg,#7C3AED,#BE1E9A)]';
const BONUS_BUTTON =
  'flex h-[44px] shrink-0 items-center justify-center rounded-all50 px-18 text-s15 font-w700 tracking-normal text-white';
/** The SaaS field's own label, so the form's few labels of its own read as the fields' do. */
const FIELD_LABEL = 'text-s13 font-w500 tracking-givmo text-text-secondary';
/** A starting amount the staffer changes in one tap: the deck's and the rulings' example ("Pledge $5"). */
const SUGGESTED_DOLLARS = '5';
/** What has to happen is the page's name too (decision 6): the fundraiser create's own bound on a name. */
const MOMENT_MAX = 100;
/** A moment's preview is asked once the words stop changing, never at every key. */
const PREVIEW_SETTLE_MS = 300;
/** How often the tab reads the game again while it shows (review-105's F1). The server's live read serves each
 *  page's state up to five seconds old (Gamified Fundraisers' `pledge_live_cache`), so at that bound the tab
 *  follows the game: a count unlocking on its own, and another organizer's act. */
const GAME_REREAD_MS = 5_000;

/** A row as the tab shows it: the read's, or an act's own answer the read hasn't caught up with yet. */
type ShownRow = BonusRow & { readState: BonusStateKind };
/** An act's answer, kept while the read still says what it said when the act was pressed. */
type Taken = { from: BonusStateKind; to: BonusStateKind };

type Dialogs =
  | { kind: 'add' }
  | { kind: 'stop'; row: ShownRow }
  | { kind: 'lock'; row: ShownRow; happened: boolean }
  | null;

export function BonusPledgesTab({
  row,
  canManage,
  enabled,
}: {
  row: OrgFundraiserOut;
  /** The game's OWNER rank with the write permission (the act's, the offer close's and the count write's). */
  canManage: boolean;
  enabled: boolean;
}) {
  const client = useQueryClient();
  const game = useQuery({
    ...publicCampaignOptions(row.id),
    enabled,
    refetchInterval: GAME_REREAD_MS,
  });
  // What each bonus pledge's own fundraiser was given (a "Donate $5" after it unlocked, D-GFU-65): the game's
  // overview lists its layers with their own totals (the editor's own read, so no request more).
  const overview = useQuery({ ...campaignOptions(row.id), enabled });
  // Each act's own answer stays on its row until the read moves on from the state it showed when the act was
  // pressed (review-105's F1): a read right after an act can still say what was, so Stop pledging and a lock
  // change the row where it stands at once, as decisions 4 and 5 draw it.
  const [taken, setTaken] = useState<ReadonlyMap<string, Taken>>(() => new Map());
  const take = (bonus: ShownRow, to: BonusStateKind) =>
    setTaken((now) => new Map(now).set(bonus.offerId, { from: bonus.readState, to }));
  const rows: ShownRow[] = bonusRows(game.data).map((bonus) => {
    const act = taken.get(bonus.offerId);
    return {
      ...bonus,
      readState: bonus.state,
      state: act && bonus.state === act.from ? act.to : bonus.state,
    };
  });
  // The game under way: its own count counts, so a moment's early end and result are taken (F4).
  const underWay = gameStillCounts(game.data, gameCountOf(game.data));
  // After the game's end the act refuses every new bonus pledge, so the tab offers none (F5).
  const ended = gameEnded(game.data);
  const canAdd = canManage && !ended;
  const givenBy = new Map(
    (overview.data?.layers ?? []).map((layer) => [
      layer.campaign_id,
      { raised: layer.raised_amount ?? null, donations: layer.donation_count ?? null },
    ]),
  );
  const gameCharity = gameCharityOf(game.data);
  const others = [
    ...new Set(
      rows
        .map((r) => r.recipientCharityId)
        .filter((id): id is string => !!id && id !== gameCharity),
    ),
  ];
  const names = useRecipientNames(
    { charity_ids: [...(gameCharity ? [gameCharity] : []), ...others] },
    enabled,
  );
  const nameOf = (id: string | null) => names.items.find((item) => item.id === id)?.name ?? null;
  const [dialog, setDialog] = useState<Dialogs>(null);
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: publicCampaignOptions(row.id).queryKey }),
      client.invalidateQueries({ queryKey: campaignOptions(row.id).queryKey }),
    ]);
  };
  const hidden = row.show_statistics === false;

  if (game.isPending || !names.ready) return <FundraisersLoading />;

  return (
    <Card flush testId="bonus-pledges">
      <CardHeader
        className="max-sm:flex-col"
        title={B.tab}
        subtitle={B.description}
        actions={
          canAdd && rows.length > 0 ? (
            <div className="flex w-full sm:w-auto">
              <Button
                appearance="saas"
                size="sm"
                className={cn(PHONE_HIT, 'whitespace-nowrap max-sm:w-full')}
                leadingIcon={<Icon icon={Plus} size={14} aria-hidden />}
                onClick={() => setDialog({ kind: 'add' })}
                testId="bonus-pledges-add"
              >
                {B.add}
              </Button>
            </div>
          ) : undefined
        }
      />
      {(!canManage || hidden) && rows.length > 0 && (
        <div
          className="flex flex-col gap-4 border-t border-border-subtle px-20 py-12"
          {...testIdProp('bonus-pledges-notes')}
        >
          {!canManage && <p className="text-s13 leading-h15 text-text-secondary">{B.viewOnly}</p>}
          {hidden && <p className="text-s13 leading-h15 text-text-secondary">{B.figuresHidden}</p>}
        </div>
      )}
      {rows.length === 0 ? (
        <div
          className="flex flex-col items-start gap-4 border-t border-border-subtle px-20 py-20"
          data-testid="bonus-pledges-empty"
        >
          <p className="text-s13 font-w600 leading-[20px] text-text-strong">{B.emptyTitle}</p>
          {/* Its line asks for one during the game, so it goes once the game has ended (F5). */}
          {!ended && <p className="text-s13 leading-h15 text-text-secondary">{B.emptyBody}</p>}
          {canAdd && (
            <Button
              appearance="saas"
              size="sm"
              className={cn(PHONE_HIT, 'mt-12')}
              leadingIcon={<Icon icon={Plus} size={14} aria-hidden />}
              onClick={() => setDialog({ kind: 'add' })}
              testId="bonus-pledges-add"
            >
              {B.add}
            </Button>
          )}
        </div>
      ) : (
        <ul className="flex flex-col" data-testid="bonus-pledges-list">
          {rows.map((bonus) => (
            <BonusRowView
              key={bonus.offerId}
              row={bonus}
              given={givenBy.get(bonus.fundraiserId) ?? null}
              forLine={
                bonus.recipientCharityId && bonus.recipientCharityId !== gameCharity
                  ? nameOf(bonus.recipientCharityId)
                  : null
              }
              canManage={canManage}
              gameUnderWay={underWay}
              onStop={() => setDialog({ kind: 'stop', row: bonus })}
              onSettle={(happened) => setDialog({ kind: 'lock', row: bonus, happened })}
            />
          ))}
        </ul>
      )}
      {dialog?.kind === 'add' && (
        <AddBonusDialog
          gameId={row.id}
          gameRead={game.data}
          gameCount={gameCountOf(game.data)}
          gameCharity={gameCharity}
          onClose={() => setDialog(null)}
          onAdded={refresh}
        />
      )}
      {dialog?.kind === 'stop' && (
        <StopDialog
          row={dialog.row}
          onTaken={() => take(dialog.row, 'pending')}
          onClose={() => setDialog(null)}
          onDone={refresh}
        />
      )}
      {dialog?.kind === 'lock' && (
        <LockDialog
          row={dialog.row}
          happened={dialog.happened}
          onTaken={() => take(dialog.row, dialog.happened ? 'unlocked' : 'not_met')}
          charity={nameOf(dialog.row.recipientCharityId ?? gameCharity) ?? ''}
          onClose={() => setDialog(null)}
          onDone={refresh}
        />
      )}
    </Card>
  );
}

/** One bonus pledge: the fans' card's words, its state as fans see it, its parts said apart, and the staff's
 *  acts: "Stop pledging" while it takes pledges, and for a moment "It happened" or "It didn't" until settled. */
function BonusRowView({
  row,
  given,
  forLine,
  canManage,
  gameUnderWay,
  onStop,
  onSettle,
}: {
  row: BonusRow;
  /** What its own fundraiser raised (the overview's layer row): donations through "Donate $5", not pledges. */
  given: { raised: string | null; donations: number | null } | null;
  forLine: string | null;
  canManage: boolean;
  /** The game's own count counts: a moment's early end and result are taken now. */
  gameUnderWay: boolean;
  onStop: () => void;
  onSettle: (happened: boolean) => void;
}) {
  const amount = wholeDollars(row.amountCents);
  const decided = row.state === 'not_met' || row.state === 'no_result';
  // The read counts uncollected pledges only, so 0 means none yet while it waits.
  const none = row.pledges === 0 && (row.state === 'open' || row.state === 'pending');
  // "Donate $5" opens only once it unlocks (D-GFU-65), so only an unlocked one has donations of its own to say.
  const gifts =
    row.state === 'unlocked' && given && given.donations !== null && given.donations > 0
      ? given
      : null;
  // Decision 7: two served figures, each in its own word. "Pledged" is the read's `pledged_cents`, its pledges
  // not yet charged ("PLEDGED, never raised"); what its fundraiser raised is its own total, the donations
  // "Donate $5" brought in and, once charged, its pledges (a charge moves one into the other in one act).
  const pledged =
    row.pledgedCents !== null && row.pledgedCents > 0 ? wholeDollars(row.pledgedCents) : null;
  const giftsLine =
    gifts && gifts.raised && amount
      ? B.giftsSince(formatFundraiserMoney(gifts.raised), gifts.donations!, amount)
      : null;
  const facts = [
    // A decided "no" owes nothing, so its amount no longer says anything.
    amount && !decided ? B.perFan(amount) : null,
    none ? B.noPledgesYet : row.pledges !== null && row.pledges > 0 ? B.pledges(row.pledges) : null,
    decided ? B.nothingCharged : pledged ? B.pledged(pledged) : null,
  ].filter((fact): fact is string => fact !== null);
  const word =
    row.state === 'open'
      ? B.open
      : row.state === 'pending'
        ? B.pending
        : row.state === 'unlocked'
          ? B.unlocked
          : row.state === 'not_met'
            ? B.didntHappen
            : B.noResult;
  // A moment is settled once it can take its result (F4): before first pitch the server takes neither its
  // result nor its early end, so the row offers only Stop pledging then.
  const settles = canSettle(row, gameUnderWay);
  const acts =
    !canManage || (!settles && row.state !== 'open') ? null : (
      // Stop is the one to hit fast mid-game: the module's destructive tone, set apart from the settling pair
      // (still behind its confirm), as the market's live tools mark their stop (Slido's red stop).
      <div className="flex flex-wrap items-center gap-x-16 gap-y-8">
        {row.state === 'open' && (
          <Button
            appearance="saas"
            tone="danger"
            size="sm"
            className={cn(PHONE_HIT, 'whitespace-nowrap')}
            onClick={onStop}
            testId={`bonus-stop-${row.offerId}`}
          >
            {B.stopPledging}
          </Button>
        )}
        {settles && (
          <div className="flex flex-wrap items-center gap-8">
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              className={cn(PHONE_HIT, 'whitespace-nowrap')}
              onClick={() => onSettle(true)}
              testId={`bonus-happened-${row.offerId}`}
            >
              {B.itHappened}
            </Button>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              className={cn(PHONE_HIT, 'whitespace-nowrap')}
              onClick={() => onSettle(false)}
              testId={`bonus-didnt-${row.offerId}`}
            >
              {B.itDidnt}
            </Button>
          </div>
        )}
      </div>
    );
  return (
    <li
      className="flex flex-col gap-12 border-t border-border-subtle px-20 py-16 lg:flex-row lg:items-center lg:justify-between lg:gap-24"
      {...testIdProp(`bonus-row-${row.offerId}`)}
    >
      <div className="min-w-0 flex-1">
        {forLine && (
          <p
            className="mb-2 text-s12 leading-[1.3] text-text-secondary"
            {...testIdProp('bonus-row-charity')}
          >
            {B.forCharity(forLine)}
          </p>
        )}
        <p
          className={cn(
            'text-s14 font-w700 leading-[1.35] text-balance',
            decided ? 'text-text-secondary' : 'text-text-strong',
          )}
        >
          {row.sentence}
        </p>
        <p
          className="mt-4 flex flex-wrap items-center gap-x-6 text-s13 leading-h15 text-text-secondary"
          {...testIdProp(`bonus-row-state-${row.offerId}`)}
        >
          <span className="inline-flex items-center gap-6 font-w600 text-text-strong">
            {/* Live now: the one fans can pledge on (the market's live tools mark it, Slido's "● Open"). */}
            {row.state === 'open' && (
              <span aria-hidden="true" className="size-8 shrink-0 rounded-all100 bg-success-500" />
            )}
            {row.state === 'pending' && (
              <Icon icon={Clock3} size={16} aria-hidden className="text-neutral-500" />
            )}
            {row.state === 'unlocked' && (
              <span
                aria-hidden="true"
                className={cn(
                  'flex size-16 items-center justify-center rounded-all100 text-white',
                  BONUS_FILL,
                )}
              >
                <Check size={11} strokeWidth={3.5} aria-hidden />
              </span>
            )}
            <span className={decided ? 'text-text-secondary' : undefined}>{word}</span>
          </span>
          {facts.map((fact) => (
            // Each fact keeps its dot, so a wrapped line never ends on one.
            <span key={fact} className="whitespace-nowrap">
              <span aria-hidden="true" className="mr-6">
                ·
              </span>
              {fact}
            </span>
          ))}
        </p>
        {giftsLine && (
          <p
            className="mt-2 text-s13 leading-h15 text-text-secondary"
            {...testIdProp('bonus-row-gifts')}
          >
            {giftsLine}
          </p>
        )}
      </div>
      {acts}
    </li>
  );
}

/** The fans' card as the game page draws it (D-FCM-70's decision 4), its button drawn, not live. */
function FansCard({
  forLine,
  sentence,
  amount,
}: {
  forLine: string | null;
  sentence: string;
  amount: string;
}) {
  return (
    <div
      className="flex w-full items-center gap-12 rounded-[20px] border border-border bg-white px-16 py-12 shadow-[0_10px_24px_rgba(0,0,0,0.04)]"
      {...testIdProp('bonus-preview-card')}
    >
      <div className="min-w-0 flex-1">
        {forLine && (
          <p className="mb-2 text-s12 leading-[1.3] text-gray80/80">{B.forCharity(forLine)}</p>
        )}
        <p className="text-s14 font-w700 leading-[1.35] text-balance text-black">{sentence}</p>
      </div>
      <span aria-hidden="true" className={cn(BONUS_BUTTON, BONUS_FILL)}>
        {B.pledgeAmount(amount)}
      </span>
    </div>
  );
}

/** Whole dollars typed as digits, in cents: integer parsing of what the staffer typed, never a float. */
const centsOfDollars = (dollars: string) =>
  /^[0-9]{1,5}$/.test(dollars) ? Number(dollars) * 100 : null;

/** The height left above a phone's keyboard (the visual viewport), for the form's sheet: its footer, the
 *  preview and the act, stays on screen while the staffer types. */
function useVisibleHeight() {
  useEffect(() => {
    const viewport = window.visualViewport;
    const root = document.documentElement;
    const set = () => {
      const height = viewport?.height ?? window.innerHeight;
      root.style.setProperty('--bonus-sheet-h', `${Math.round(height)}px`);
      // The keyboard's share of the layout viewport (iOS keeps the layout viewport under it).
      const keyboard = Math.max(0, window.innerHeight - height - (viewport?.offsetTop ?? 0));
      root.style.setProperty('--bonus-keyboard', `${Math.round(keyboard)}px`);
    };
    set();
    viewport?.addEventListener('resize', set);
    return () => {
      viewport?.removeEventListener('resize', set);
      root.style.removeProperty('--bonus-sheet-h');
      root.style.removeProperty('--bonus-keyboard');
    };
  }, []);
}

/** Typing on a phone: the field to the top of the sheet's fields, so the next field shows whole above the
 *  preview, never cut by it. A short form (the charity console's, with no charity to choose) can't scroll that
 *  far, so it gets the room it lacks under its fields: no line of the field above is left cut under the title. */
function fieldToTop(field: HTMLElement) {
  const body = field.closest<HTMLElement>('[data-bonus-scroll]');
  const room = body?.querySelector<HTMLElement>('[data-bonus-room]');
  if (!body || !room) return;
  room.style.height = '0px';
  const top =
    field.getBoundingClientRect().top -
    body.getBoundingClientRect().top +
    body.scrollTop -
    parseFloat(getComputedStyle(field).scrollMarginTop);
  room.style.height = `${Math.max(0, Math.ceil(top - (body.scrollHeight - body.clientHeight)))}px`;
  field.scrollIntoView({ block: 'start' });
}

/** A refusal about one field (under it, its border red) or about the whole act (`field` null, by the act). */
type Refusal = { field: 'moment' | 'count' | 'recipient' | null; message: string } | null;

/** The preview's body (`POST /pledge-offers/rule-sentence`): the act's own terms, with the game as the host, so
 *  nothing is written. A moment is a new yes/no on the game; its dates change no word of the sentence, so any
 *  span from now serves. */
export function previewBodyOf(
  gameId: string,
  kind: 'moment' | 'count',
  moment: string,
  atLeast: number | null,
  amountCents: number,
  gameCount: GameCount | null,
  now: Date = new Date(),
): PledgeOfferCreate | null {
  const terms = {
    fundraiser_id: gameId,
    amount_rule: 'flat' as const,
    rate_policy: 'fixed' as const,
    suggested_rate_cents: amountCents,
    default_cap_cents: amountCents,
  };
  if (kind === 'moment') {
    if (!gameCount || moment.trim() === '') return null;
    return {
      ...terms,
      condition_kind: 'yes',
      measure: {
        fundraiser_id: gameId,
        kind: 'yes_no',
        unit_label: 'time',
        subject_name: gameCount.subject,
        subject_affiliation: 'official',
        count_phrase: moment.trim(),
        starts_at: now.toISOString(),
        ends_at: new Date(now.getTime() + 4 * 60 * 60 * 1000).toISOString(),
        health_data: false,
      },
    };
  }
  if (!gameCount || atLeast === null) return null;
  return {
    ...terms,
    measure_id: gameCount.measureId,
    condition_kind: 'at_least',
    condition_value: String(atLeast),
  };
}

function AddBonusDialog({
  gameId,
  gameRead,
  gameCount,
  gameCharity,
  onClose,
  onAdded,
}: {
  gameId: string;
  gameRead: unknown;
  gameCount: GameCount | null;
  gameCharity: string | null;
  onClose: () => void;
  onAdded: () => Promise<void>;
}) {
  useVisibleHeight();
  const { flavor } = useFundraisersModule();
  // A company's game whose pages pick their charities (Shape A: `recipient_choice` `supporters`) may pay
  // another charity, from the list that binds them (`binding_list`, none: any charity); a game that picks its
  // own pays the game's, and a charity's raises only for itself (Q8): each shows the name, with no choice, and
  // sends none (the server's join rule refuses a pick there).
  const read = gameRead as
    | { recipient_choice?: string | null; binding_list?: { charity_ids?: string[] } | null }
    | undefined;
  const chooses = flavor.recipientMode === 'chosen' && read?.recipient_choice === 'supporters';
  const listed = read?.binding_list?.charity_ids ?? [];
  const list = useRecipientNames(
    chooses && listed.length > 0
      ? { charity_ids: listed }
      : gameCharity
        ? { charity_ids: [gameCharity] }
        : null,
  );
  // Where no list binds the game's pages (its season lets each page pick any charity), the staffer searches
  // every charity, the game's own first and chosen (the module's own charity search, charities only).
  const searches = chooses && listed.length === 0;
  const [search, setSearch] = useState('');
  const found = useQuery({
    ...charitiesGetCharitiesOptions({ query: { q: search.trim() || null, 'page[limit]': 5 } }),
    enabled: searches && search.trim().length > 0,
  });
  const foundRows = searches
    ? extractCharityResults(found.data).filter((result) => result.id !== gameCharity)
    : [];
  const counts = gameStillCounts(gameRead, gameCount);
  const [kind, setKind] = useState<'moment' | 'count'>('moment');
  const [moment, setMoment] = useState('');
  const [atLeast, setAtLeast] = useState('');
  // A suggested amount the staffer can change: one tap fewer mid-game (the set's README says why $5).
  const [amount, setAmount] = useState(SUGGESTED_DOLLARS);
  const [recipientCharityId, setRecipientCharityId] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<Refusal>(null);
  const [scrolled, setScrolled] = useState(false);
  const live = useLivePage();
  // One key per press series: kept while no answer came (asked again under it), new after a refusal, which
  // wrote nothing, since the next press sends what the staffer changed.
  const key = useRef(crypto.randomUUID());
  const chosen = recipientCharityId ?? gameCharity;
  const units = gameCount ? unitsOf(gameCount.unit) : '';
  const amountCents = centsOfDollars(amount);
  const number = /^[0-9]{1,6}$/.test(atLeast) && Number(atLeast) > 0 ? Number(atLeast) : null;
  const complete =
    amountCents !== null &&
    amountCents > 0 &&
    (kind === 'moment' ? moment.trim().length > 0 : number !== null);
  // The preview's dates are the dialog's own, fixed when it opens, so a settled body is asked once.
  const [opened] = useState(() => new Date());
  const previewBody = useSettled(
    complete && amountCents !== null
      ? previewBodyOf(gameId, kind, moment, number, amountCents, gameCount, opened)
      : null,
    PREVIEW_SETTLE_MS,
  );
  const preview = useQuery({
    queryKey: ['fundraisers-module', 'bonus-preview', previewBody],
    queryFn: ({ signal }) => previewBonusSentence(previewBody!, signal),
    enabled: previewBody !== null,
    // A refusal is the preview's answer; a lost one is asked again, twice at most (review-105's F3).
    retry: (failures, error) => askAgain(error) && failures < 2,
  });
  // The preview refuses a word as it's typed (GFU-BE84), in the server's sentence: the same refusal as the
  // act's, shown the same way. Only a refusal counts (a 4xx it would answer again the same way): a lost or
  // failed answer draws no preview and leaves the act as it was, since the act never needs it (F3).
  const previewRefusal: Refusal =
    preview.isError &&
    isGivmoError(preview.error) &&
    !askAgain(preview.error) &&
    preview.error.status !== undefined &&
    preview.error.status >= 400 &&
    preview.error.status < 500 &&
    kind === 'moment' &&
    preview.error.message
      ? { field: 'moment', message: preview.error.message }
      : null;
  const standing = refusal ?? previewRefusal;
  const served = previewSentenceOf(preview.data);
  const sentence = served ? `${served}.` : null;
  const body: BonusPledgeBody | null =
    amountCents === null
      ? null
      : kind === 'moment'
        ? {
            kind,
            moment: moment.trim(),
            amount_cents: amountCents,
            ...(chooses && chosen ? { charity_id: chosen } : {}),
          }
        : gameCount && number !== null
          ? {
              kind,
              measure_id: gameCount.measureId,
              at_least: number,
              amount_cents: amountCents,
              ...(chooses && chosen ? { charity_id: chosen } : {}),
            }
          : null;
  const add = useMutation({
    mutationFn: () => untilAnswered(() => addBonusPledge(gameId, body!, key.current), live),
    onSuccess: async () => {
      key.current = crypto.randomUUID();
      await onAdded();
      if (!live()) return;
      onClose();
      toast.success(B.added);
    },
    onError: async (error) => {
      if (!askAgain(error)) key.current = crypto.randomUUID();
      if (!live()) return;
      if (!isGivmoError(error) || askAgain(error) || !error.message) {
        toast.error(B.failed);
        return;
      }
      if (firstErrorCode(error) === 'PLEDGE_COUNT_REACHED') {
        // The count moved while the form was open: read it again, beside the server's sentence.
        await onAdded();
        setRefusal({ field: 'count', message: error.message });
      } else if (error.message === BONUS_SERVER_WORDS.charityUnavailable) {
        setRefusal({ field: 'recipient', message: error.message });
      } else {
        // About the whole act (the game's end passed, its count over): by the act, in the server's sentence,
        // with no next step of its own. "Say it another way." stays the preview's, which screens the words as
        // they're typed (review-105's F5).
        setRefusal({ field: null, message: error.message });
      }
    },
  });
  const options = list.items.map((item) => ({ value: item.id, label: item.name }));
  const chosenName =
    list.items.find((item) => item.id === chosen)?.name ??
    foundRows.find((result) => result.id === chosen)?.name ??
    null;
  const forLine = chosen && chosen !== gameCharity ? chosenName : null;
  // Every refusal reads the same way: the server's sentence, then what to do next; under its field when it is
  // about one, by the act when it is about the whole act. The act waits until something changes, so it is
  // never pressed again unchanged.
  const next =
    standing?.field === 'count' && gameCount?.count != null
      ? B.reachedNext(gameCount.subject, gameCount.count)
      : standing?.field === 'recipient'
        ? B.charityNext
        : standing?.field === 'moment'
          ? B.bettingNext
          : null;
  const refusalLine = standing
    ? `${standing.message.replace(/\.?$/, '.')} ${next ?? ''}`.trim()
    : null;
  const ready = complete && body !== null && standing === null && list.ready;
  const change = () => setRefusal(null);
  // The design system's one error look (its mark, then the words; an alert), right under the field it is
  // about, 6 px below it as the field's own line would sit.
  const refusalUnder = (field: 'moment' | 'count' | 'recipient', inGap = false) =>
    standing?.field === field && refusalLine ? (
      <ErrorMessage className={inGap ? 'pt-0' : 'pt-6'} testId={`bonus-refusal-${field}`}>
        <span id={`bonus-refusal-${field}-words`}>{refusalLine}</span>
      </ErrorMessage>
    ) : null;

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open && !add.isPending) onClose();
      }}
    >
      <Dialog.Content
        aria-label={B.dialogTitle}
        width={480}
        testId="bonus-add-dialog"
        className={cn(
          'fundraisers-dialog flex flex-col gap-0 overflow-hidden p-0',
          // A phone's form is a sheet from the bottom, as tall as its content up to the room above the keyboard,
          // so its foot, the preview and the act, stays in view while the staffer types, and no blank band sits
          // between the fields and the act.
          'max-sm:top-auto max-sm:bottom-[var(--bonus-keyboard,0px)] max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0',
          'max-sm:w-screen! max-sm:max-w-none max-sm:h-auto max-sm:max-h-[var(--bonus-sheet-h,100dvh)]',
          'max-sm:rounded-none max-sm:rounded-t-[20px]',
        )}
      >
        <div className="px-24 pb-12 pt-24 max-sm:px-16 max-sm:pt-20">
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {B.dialogTitle}
          </Dialog.Title>
        </div>
        <div
          className="min-h-0 flex-1 overflow-y-auto px-24 max-sm:px-16"
          onScroll={(event) => setScrolled(event.currentTarget.scrollTop > 0)}
          data-bonus-scroll=""
          data-testid="bonus-add-dialog-scroll"
        >
          {/* Its edges fade into the sheet (the top once scrolled): a field cut there reads as more to scroll
              to, never as a broken box. Overlays, not a mask, so the fields' text renders as everywhere else. */}
          {scrolled && (
            <div
              aria-hidden="true"
              className="pointer-events-none sticky top-0 z-10 -mb-16 h-16 bg-gradient-to-b from-white to-transparent"
            />
          )}
          <div className="flex flex-col gap-16 pb-16">
            <div className="flex flex-col gap-6">
              <span className={FIELD_LABEL}>{B.whatUnlocks}</span>
              <RadioTileGroup
                value={kind}
                onValueChange={(value) => {
                  setKind(value as 'moment' | 'count');
                  change();
                }}
                aria-label={B.whatUnlocks}
                testId="bonus-kind"
              >
                <RadioTile
                  value="moment"
                  label={B.kindMoment}
                  description={B.kindMomentLine}
                  testId="bonus-kind-moment"
                />
                {gameCount && counts && (
                  <RadioTile
                    value="count"
                    label={B.kindCount(units)}
                    description={B.kindCountLine}
                    testId="bonus-kind-count"
                  />
                )}
              </RadioTileGroup>
            </div>
            {kind === 'moment' ? (
              <div className="flex scroll-mt-16 flex-col" data-bonus-field="">
                <Field
                  label={B.momentLabel}
                  value={moment}
                  maxLength={MOMENT_MAX}
                  autoComplete="off"
                  // The example helps only while the field is empty: typed, it would repeat the typed words.
                  helper={moment.length === 0 ? B.momentExample : undefined}
                  error={standing?.field === 'moment'}
                  onFocus={(event) => {
                    const input = event.currentTarget;
                    const field = input.closest<HTMLElement>('[data-bonus-field]');
                    if (window.innerWidth >= 640 || !field) return;
                    const toTop = () => fieldToTop(field);
                    toTop();
                    // The keyboard rises after the focus and shortens the sheet: again each time, while typing.
                    window.visualViewport?.addEventListener('resize', toTop);
                    // Typing done, the sheet is as tall as its fields again: the room it was lent goes.
                    input.addEventListener(
                      'blur',
                      () => {
                        window.visualViewport?.removeEventListener('resize', toTop);
                        const room = field
                          .closest('[data-bonus-scroll]')
                          ?.querySelector<HTMLElement>('[data-bonus-room]');
                        if (room) room.style.height = '0px';
                      },
                      { once: true },
                    );
                  }}
                  {...(standing?.field === 'moment'
                    ? { 'aria-describedby': 'bonus-refusal-moment-words' }
                    : {})}
                  onChange={(event) => {
                    setMoment(event.target.value);
                    change();
                  }}
                  testId="bonus-moment"
                />
                {refusalUnder('moment')}
              </div>
            ) : (
              <div className="flex flex-col">
                <Field
                  label={B.atLeast}
                  value={atLeast}
                  inputMode="numeric"
                  autoComplete="off"
                  trailing={
                    <span className="whitespace-nowrap pr-4 text-s13 text-text-secondary">
                      {units}
                    </span>
                  }
                  helper={
                    gameCount?.count != null && standing?.field !== 'count'
                      ? B.countNow(gameCount.subject, gameCount.count)
                      : undefined
                  }
                  error={standing?.field === 'count'}
                  {...(standing?.field === 'count'
                    ? { 'aria-describedby': 'bonus-refusal-count-words' }
                    : {})}
                  onChange={(event) => {
                    setAtLeast(event.target.value.replace(/[^0-9]/g, ''));
                    change();
                  }}
                  testId="bonus-at-least"
                />
                {refusalUnder('count')}
              </div>
            )}
            <Field
              label={B.amount}
              value={amount}
              inputMode="numeric"
              autoComplete="off"
              leading={<span className="text-s14 text-text-secondary">$</span>}
              trailing={
                <span className="whitespace-nowrap pr-4 text-s13 text-text-secondary">
                  {B.aFan}
                </span>
              }
              className="pl-24"
              containerClassName="sm:max-w-[200px]"
              onChange={(event) => {
                setAmount(event.target.value.replace(/[^0-9]/g, ''));
                change();
              }}
              testId="bonus-amount"
            />
            {searches ? (
              <div
                className="flex scroll-mb-16 flex-col gap-6"
                {...testIdProp('bonus-charity-search-group')}
              >
                <span className={FIELD_LABEL}>{B.charity}</span>
                {/* The game's own charity first, marked as such, and apart from the search: it is the usual pick
                    and matches no search, so it never sits among the results. */}
                <RadioTileGroup
                  value={chosen === gameCharity ? (gameCharity ?? '') : ''}
                  onValueChange={(value) => {
                    setRecipientCharityId(value as string);
                    change();
                  }}
                  aria-label={B.gamesCharity}
                  testId="bonus-charity-game-group"
                >
                  {gameCharity && (
                    <RadioTile
                      value={gameCharity}
                      label={list.items.find((item) => item.id === gameCharity)?.name ?? ''}
                      description={B.gamesCharity}
                      testId="bonus-charity-game-option"
                    />
                  )}
                </RadioTileGroup>
                <span className={cn(FIELD_LABEL, 'mt-10')}>{B.orAnotherCharity}</span>
                <SearchInput
                  value={search}
                  containerClassName="w-full"
                  placeholder={B.charitySearch}
                  aria-label={B.charitySearch}
                  onChange={(event) => setSearch(event.target.value)}
                  onClear={() => setSearch('')}
                  testId="bonus-charity-search"
                />
                {foundRows.length > 0 && (
                  // Each with the line the module's charity picker shows under a result (its category), so two
                  // look-alike names read apart (the search read serves no city or EIN).
                  <RadioTileGroup
                    value={chosen && chosen !== gameCharity ? chosen : ''}
                    onValueChange={(value) => {
                      setRecipientCharityId(value as string);
                      change();
                    }}
                    aria-label={B.charitySearch}
                    className="mt-4"
                    testId="bonus-charity-results"
                  >
                    {foundRows.map((result) => (
                      <RadioTile
                        key={result.id}
                        value={result.id}
                        label={result.name}
                        description={result.subtitle ?? undefined}
                        testId={`bonus-charity-option-${result.id}`}
                      />
                    ))}
                  </RadioTileGroup>
                )}
                {search.trim().length > 0 && found.isSuccess && foundRows.length === 0 && (
                  <p className="text-s13 leading-h15 text-text-secondary">{B.noCharityFound}</p>
                )}
                {refusalUnder('recipient', true)}
              </div>
            ) : chooses ? (
              <div className="flex flex-col gap-6">
                <Select
                  appearance="saas"
                  label={B.charity}
                  options={options}
                  value={chosen}
                  onValueChange={(value) => {
                    setRecipientCharityId(value);
                    change();
                  }}
                  error={standing?.field === 'recipient'}
                  testId="bonus-charity"
                />
                {refusalUnder('recipient', true)}
                {chosen === gameCharity && (
                  <p
                    className="text-s13 leading-h15 text-text-secondary"
                    {...testIdProp('bonus-charity-game')}
                  >
                    {B.gamesCharity}
                  </p>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-6" {...testIdProp('bonus-charity-fixed')}>
                <span className={FIELD_LABEL}>{B.charity}</span>
                <p className="text-s14 leading-h15 text-text-strong">{chosenName}</p>
              </div>
            )}
          </div>
          <div aria-hidden="true" data-bonus-room="" />
          <div
            aria-hidden="true"
            className="pointer-events-none sticky bottom-0 z-10 -mt-16 h-16 bg-gradient-to-t from-white to-transparent"
          />
        </div>
        <div
          className="flex flex-col gap-12 border-t border-border-subtle px-24 pb-24 pt-16 max-sm:px-16 max-sm:pb-16"
          {...testIdProp('bonus-add-footer')}
        >
          {standing && standing.field === null && refusalLine ? (
            <ErrorMessage className="pt-0" testId="bonus-refusal">
              {refusalLine}
            </ErrorMessage>
          ) : sentence && amountCents !== null && !standing ? (
            <div className="flex flex-col gap-6" {...testIdProp('bonus-preview')}>
              <span className={FIELD_LABEL}>
                {kind === 'count' && number !== null && gameCount
                  ? B.fansPledgeUntilCount(gameCount.subject, number)
                  : B.fansPledgeUntilEnd}
              </span>
              <FansCard
                forLine={forLine}
                sentence={sentence}
                amount={wholeDollars(amountCents) ?? ''}
              />
            </div>
          ) : null}
          <div className="flex items-center justify-end gap-8">
            <Button
              appearance="saas"
              tone="secondary"
              disabled={add.isPending}
              onClick={onClose}
              testId="bonus-add-cancel"
            >
              {B.cancel}
            </Button>
            <Button
              appearance="saas"
              loading={add.isPending}
              disabled={!ready || add.isPending}
              onClick={() => add.mutate()}
              testId="bonus-add-confirm"
            >
              {B.add}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function StopDialog({
  row,
  onTaken,
  onClose,
  onDone,
}: {
  row: BonusRow;
  /** Its answer on its row at once: Pending, where it stands (F1). */
  onTaken: () => void;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const live = useLivePage();
  const key = useRef(crypto.randomUUID());
  const stop = useMutation({
    mutationFn: () => untilAnswered(() => stopBonusPledging(row.offerId, key.current), live),
    onSuccess: async () => {
      onTaken();
      await onDone();
      if (!live()) return;
      onClose();
      toast.success(B.stopped);
    },
    onError: async (error) => {
      await onDone();
      if (live())
        toast.error(
          isGivmoError(error) && !askAgain(error) && error.message ? error.message : B.stopFailed,
        );
    },
  });
  return (
    <BonusConfirm
      title={B.stopTitle}
      sentence={row.sentence}
      body={B.stopBody}
      confirm={B.stopPledging}
      // The row's own destructive red, so the act reads the same in its confirm.
      danger
      busy={stop.isPending}
      onConfirm={() => stop.mutate()}
      onClose={onClose}
      testId="bonus-stop-dialog"
    />
  );
}

function LockDialog({
  row,
  happened,
  onTaken,
  charity,
  onClose,
  onDone,
}: {
  row: BonusRow;
  happened: boolean;
  /** Its answer on its row at once: Unlocked or Didn't happen, where it stands (F1). */
  onTaken: () => void;
  charity: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const live = useLivePage();
  const key = useRef(crypto.randomUUID());
  const lock = useMutation({
    mutationFn: () =>
      untilAnswered(
        () => lockMomentResult(row.measureId, happened, row.counting, key.current),
        live,
      ),
    onSuccess: async () => {
      onTaken();
      await onDone();
      if (!live()) return;
      onClose();
      toast.success(happened ? B.lockedHappened : B.lockedDidnt);
    },
    onError: async (error) => {
      // A result locked by someone else meanwhile: the read says what stands, beside the server's sentence.
      await onDone();
      if (live())
        toast.error(
          isGivmoError(error) && !askAgain(error) && error.message ? error.message : B.lockFailed,
        );
    },
  });
  // The pledged total is the server's figure; where the game hides its figures the line names none.
  const total = wholeDollars(row.pledgedCents);
  const body = happened
    ? row.pledges === 0
      ? // Nobody to email and nothing to go anywhere: the Copy's own lines (review-105's F7).
        B.lockHappenedBodyNone
      : row.pledges !== null && total !== null
        ? B.lockHappenedBody(row.pledges, total, charity)
        : B.lockHappenedBodyHidden(charity)
    : B.lockDidntBody;
  return (
    <BonusConfirm
      title={happened ? B.lockHappenedTitle : B.lockDidntTitle}
      sentence={row.sentence}
      // Settled while it still takes pledges, the lock stops them first, and says so.
      body={row.state === 'open' ? `${B.stopsNow} ${body}` : body}
      confirm={B.lockResult}
      busy={lock.isPending}
      onConfirm={() => lock.mutate()}
      onClose={onClose}
      testId={happened ? 'bonus-lock-happened-dialog' : 'bonus-lock-didnt-dialog'}
    />
  );
}

/** The module's confirm (`confirm-dialog.tsx`'s treatment), with the bonus pledge it acts on named first,
 *  in its card's own words, so a game with several never confirms the wrong one. */
function BonusConfirm({
  title,
  sentence,
  body,
  confirm,
  danger = false,
  busy,
  onConfirm,
  onClose,
  testId,
}: {
  title: string;
  sentence: string;
  body: string;
  confirm: string;
  danger?: boolean;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
  testId: string;
}) {
  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
    >
      <Dialog.Content className="fundraisers-dialog flex flex-col gap-16" testId={testId}>
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {title}
        </Dialog.Title>
        <p className="rounded-lg border border-border-subtle bg-surface-sunken px-12 py-10 text-s14 font-w700 leading-[1.35] text-text-strong">
          {sentence}
        </p>
        <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-pretty text-text-secondary">
          {body}
        </Dialog.Description>
        <div className="mt-8 flex justify-end gap-8">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={busy}
            testId={`${testId}-cancel`}
            onClick={onClose}
          >
            {B.cancel}
          </Button>
          <Button
            appearance="saas"
            tone={danger ? 'danger' : 'primary'}
            loading={busy}
            disabled={busy}
            testId={`${testId}-confirm`}
            onClick={onConfirm}
          >
            {confirm}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
